/**
 * One logo slot in Settings -> Appearance: a preview on the surface the file is
 * meant for (white for the light theme, near-black for the dark one) plus pick /
 * replace / remove.
 *
 * The same field serves both marks - the wide header logo and the square one the
 * collapsed sidebar shows - because the only thing that differs between them is
 * the copy. `kind` picks the message keys; everything else is identical.
 *
 * The two controls sit UNDER the tile rather than floating on it. A circular
 * `IconBtn` inherits `--color-muted-foreground`, which in the dark theme is a
 * pale grey - on the white tile the light slot deliberately keeps, the remove
 * button would have been white on white.
 *
 * Its own file so a keystroke in the form cannot remount it, and so the page
 * next door stays about the page rather than about a field.
 */
import { ImagePlus, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { Btn } from "../../ui";

export type LogoSlot = "light" | "dark";
/** Which mark the slot belongs to: the wide header logo or the square rail one. */
export type LogoKind = "logo" | "icon";

export function BrandingLogoSlot({
  kind, slot, url, height, canPick, onPick, onClear,
}: {
  kind: LogoKind;
  slot: LogoSlot;
  url: string;
  height: number;
  canPick: boolean;
  onPick: () => void;
  onClear: () => void;
}) {
  const t = useT();
  const light = slot === "light";
  const key = `settings.appearance.${kind}${light ? "Light" : "Dark"}`;
  const remove = t("settings.appearance.logoRemove");

  return (
    <div className="min-w-0">
      <span className="crm-lab !mt-0">{t(key)}</span>

      {url ? (
        // The tile keeps its own colour in both themes - it is standing in for
        // the background the file was drawn for, not for the app's surface. The
        // hairline is what stops the white one vanishing into a light card.
        <div
          className={cn(
            "flex h-[104px] items-center justify-center rounded-[24px] border border-[var(--crm-hair)] px-6",
            light ? "bg-white" : "bg-neutral-900",
          )}
        >
          <img src={url} alt="" style={{ height, width: "auto" }} className="max-h-[76px] max-w-full object-contain" />
        </div>
      ) : (
        <button
          type="button"
          onClick={onPick}
          disabled={!canPick}
          className="flex h-[104px] w-full flex-col items-center justify-center gap-2 rounded-[24px] bg-[var(--crm-sunk)] text-[12.5px] font-[550] text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ImagePlus className="size-5" />
          {t("settings.appearance.logoPick")}
        </button>
      )}

      <p className="crm-sub mt-2.5 text-[11.5px] leading-snug">{t(`${key}Hint`)}</p>

      {url ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <Btn small onClick={onPick} disabled={!canPick}>
            <ImagePlus className="size-3.5" />
            {t("settings.appearance.logoChange")}
          </Btn>
          <Btn small tone="danger" onClick={onClear} title={remove} aria-label={remove}>
            <Trash2 className="size-3.5" />
          </Btn>
        </div>
      ) : null}
    </div>
  );
}
